import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  taxesQuery, daysFromToday, plurale, isSuperAdmin, TAX_KIND_LABEL, TAX_SOURCE_LABEL,
  type AdminOverview, type TaxObligation, type TaxInstallment,
} from './adminApi';
import { useAdminActions } from './useAdminActions';
import { SettlePopover } from './SettlePopover';

/**
 * Fisco — le scadenze da pagare, e basta.
 *   IVA                     → una riga sola; il dettaglio per anno si apre a mano
 *   F24 e altre scadenze    → obbligazioni con rate: aggiungi, modifica, paga
 *
 * UNA SOLA CIFRA IVA IN EVIDENZA: l'IVA scaduta, quella della stat-band in alto
 * (overdueTotal, con sotto i trimestri non ancora scaduti). Prima la pagina
 * mostrava 96.771 qui e 86.296 nella band — la stessa cosa contata in due modi,
 * il residuo con e senza il trimestre in corso — e non si capiva quale fosse
 * quella vera. La tabella per anno resta, ma chiusa: dice una terza volta quello
 * che già dicono le righe dei trimestri e il piano di rientro.
 *
 * Le scadenze fiscali non subiscono la data di taglio dell'Amministrazione: il
 * piano AdER e l'IVA arretrata del 2023 si pagano adesso.
 */
export function FiscoTab({ ov, canWrite }: { ov: AdminOverview | undefined; canWrite: boolean }) {
  const user = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(user);
  const openModal = useModalStore((s) => s.open);
  const q = useQuery(taxesQuery);
  const [showByYear, setShowByYear] = useState(false);

  const byYear = ov?.iva.byYear ?? [];
  const taxes = useMemo(() => (q.data ?? [])
    .filter((t) => superAdmin || t.entity !== 'PERSONALE_ANDREA')
    .filter((t) => !['PAGATA', 'PAGATO', 'CHIUSA'].includes(String(t.status).toUpperCase()))
    .sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999')), [q.data, superAdmin]);

  // La riga IVA di un anno somma più obbligazioni (liquidazioni FiC pagate + stima
  // del residuo): la matita apre quella da confermare (STIMA con residuo), altrimenti
  // la prima ancora aperta, altrimenti la prima dell'anno.
  const ivaObligationFor = (year: number) => {
    const xs = (q.data ?? []).filter((t) => t.year === year && (t.kind === 'IVA_ARRETRATA' || t.kind === 'IVA_TRIMESTRALE'));
    const open = (t: TaxObligation) => Number(t.residual ?? t.amountDue - (t.amountPaid ?? 0)) > 0;
    return xs.find((t) => t.source === 'STIMA' && open(t)) ?? xs.find(open) ?? xs[0];
  };

  return (
    <>
      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>Le cifre <strong>Stima</strong> vengono dal report di settembre 2026 e vanno confermate con il commercialista.</span>
      </div>

      {/* ── F24 e altre scadenze ── */}
      <div className="ed-section">
        <div className="data-hd">
          <span className="t-micro">F24 e altre scadenze</span>
          <div className="flex items-center gap-2">
            <span className="mono mute text-[12px]">{plurale(taxes.length, 'aperta', 'aperte')}</span>
            {/* Sul telefono lo stesso bottone sta in fondo (mobile.md §4). */}
            {canWrite && (
              <button className="btn btn-sm d-only" onClick={() => openModal('newTax')}>
                <Icon name="plus" size={10} /> Nuova scadenza
              </button>
            )}
          </div>
        </div>
        {q.isLoading && !q.data ? (
          <table className="tbl tbl--ed"><tbody><SkeletonTable rows={4} cols={5} withAvatar={false} /></tbody></table>
        ) : taxes.length === 0 ? (
          <EmptyState icon="calendarClock" title="Nessuna scadenza aperta" description="F24, IVA, INPS e rate: le aggiungi da qui e le paghi quando arrivano." />
        ) : (
          /* Scheda sul telefono. La riga si apre solo quando l'obbligo ha delle
             rate, cioè quasi mai: il chevron sarebbe una promessa sbagliata su
             tutte le altre, quindi `tbl--static` e le rate si aprono col tocco
             sulla scheda come sul computer. */
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th>Scadenza</th>
                <th style={{ width: 130 }}>Data</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: 130 }}>Fonte</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {taxes.map((t) => <TaxRow key={t.id} t={t} canWrite={canWrite} />)}
            </tbody>
          </table>
        )}
      </div>

      {/* ── IVA per anno: in fondo e chiusa, è il dettaglio di una cifra già in alto ── */}
      <div className="adm-fold">
        <button className="btn btn-sm btn-ghost" aria-expanded={showByYear} onClick={() => setShowByYear((v) => !v)}>
          <Icon name={showByYear ? 'chevronU' : 'chevronD'} size={10} /> {showByYear ? 'Nascondi' : 'Mostra'} il dettaglio IVA per anno
        </button>
        {showByYear && (byYear.length === 0 ? (
          <EmptyState icon="percent" title="Nessun dato IVA" description="Quando FattureInCloud è sincronizzato, l'IVA maturata per anno compare qui." />
        ) : (
          /* Dovuta, versata, residua: quattro cifre che si leggono solo
             confrontandole riga per riga. È il caso di mobile.md §8. */
          <DesktopOnly
            cosa="Il dettaglio dell'IVA per anno"
            perche="sono quattro cifre per riga che hanno senso solo confrontate fra loro, e su uno schermo stretto il confronto si perde."
          >
          <table className="tbl tbl--ed">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Anno</th>
                <th className="num">Dovuta</th>
                <th className="num">Versata</th>
                <th className="num">Residua</th>
                <th style={{ width: 130 }}>Fonte</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {byYear.map((y) => {
                const obl = y.id ? (q.data ?? []).find((t) => t.id === y.id) : ivaObligationFor(y.year);
                return (
                  <tr key={y.year} style={{ cursor: 'default' }}>
                    <td className="mono font-semibold">{y.year}</td>
                    <td className="num mono">€ {fmtEur(y.due)}</td>
                    <td className="num mono mute">€ {fmtEur(y.paid)}</td>
                    <td className={`num mono font-semibold ${y.residual > 0 ? '' : 'mute'}`}>€ {fmtEur(y.residual)}</td>
                    <td><Chip tone={y.source === 'STIMA' ? 'warn' : 'ghost'} dot>{TAX_SOURCE_LABEL[y.source] ?? y.source}</Chip></td>
                    <td>
                      {canWrite && (
                        <div className="flex justify-end">
                          <button className="btn btn-sm btn-ghost btn-icon" title={obl ? 'Modifica la cifra' : 'Registra la cifra confermata'} aria-label="Modifica"
                            onClick={() => openModal('newTax', obl
                              ? { tax: obl }
                              : { prefill: { kind: 'IVA_ARRETRATA', year: y.year, description: `IVA ${y.year}`, amountDue: y.residual, source: 'CONFERMATO' } })}>
                            <Icon name="pencil" size={12} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </DesktopOnly>
        ))}
      </div>

      {canWrite && (
        <DesktopOnly
          cosa="La creazione di una nuova scadenza fiscale"
          perche="richiede importi, rate e riferimenti da verificare; dal telefono puoi segnare pagata una scadenza esistente."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={() => openModal('newTax')}>
              <Icon name="plus" size={12} /> Nuova scadenza
            </button>
          </div>
        </DesktopOnly>
      )}
    </>
  );
}

function TaxRow({ t, canWrite }: { t: TaxObligation; canWrite: boolean }) {
  const openModal = useModalStore((s) => s.open);
  const { pay, remove } = useAdminActions();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const inst = (t.installments ?? []).slice().sort((a, b) => a.seq - b.seq);
  const openInst = inst.filter((i) => !i.paidAt);
  const residual = Number(t.residual ?? (t.amountDue - (t.amountPaid ?? 0))) || 0;
  const nextDue = openInst[0]?.dueDate ?? t.nextDueDate ?? t.dueDate;
  // "Paga" sulla riga salda la prossima rata aperta (ogni obbligo ne ha almeno una)
  const nextInst = openInst[0];
  const days = daysFromToday(nextDue);
  const overdue = days != null && days < 0;
  const soon = days != null && days >= 0 && days <= 7;
  const title = t.description || `${TAX_KIND_LABEL[t.kind] ?? t.kind}${t.period ? ` ${t.period}` : ''} ${t.year}`;
  return (
    <>
      <tr onClick={() => inst.length > 0 && setExpanded((v) => !v)} style={{ cursor: inst.length ? 'pointer' : 'default' }}>
        <td data-m="hd">
          <div className="font-semibold flex items-center gap-2">
            {title}
            {t.entity === 'PERSONALE_ANDREA' && <Chip tone="ghost">Personale</Chip>}
          </div>
          <div className="text-[12px] mute">
            {TAX_KIND_LABEL[t.kind] ?? t.kind}
            {inst.length > 0 && ` · ${plurale(openInst.length, 'rata', 'rate')} su ${inst.length}`}
          </div>
        </td>
        <td data-m="key" data-l="Data" className={`mono text-[13px] ${overdue ? 'text-alert font-semibold' : soon ? 'text-warn' : 'mute'}`}>{fmtDate(nextDue)}</td>
        <td data-m="key" data-l="Importo" className="num mono font-semibold">€ {fmtEur(residual, true)}</td>
        <td data-m="key" data-l="Fonte"><Chip tone={t.source === 'STIMA' ? 'warn' : 'ghost'} dot>{TAX_SOURCE_LABEL[t.source] ?? t.source}</Chip></td>
        <td data-m="row" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end gap-1 relative">
            {canWrite && openInst.length <= 1 && residual > 0 && (
              <button className="btn btn-sm" disabled={pay.isPending} onClick={() => setOpen((v) => !v)}>
                <Icon name="check" size={10} /> Paga
              </button>
            )}
            {canWrite && (
              <>
                <button className="btn btn-sm btn-ghost btn-icon" title="Modifica" aria-label="Modifica" onClick={() => openModal('newTax', { tax: t })}>
                  <Icon name="pencil" size={12} />
                </button>
                <button className="btn btn-sm btn-ghost btn-icon" title="Elimina" aria-label="Elimina"
                  onClick={() => remove.mutate({ path: `/admin/taxes/${t.id}`, title: 'Eliminare questa scadenza?', body: title, okTitle: 'Scadenza eliminata' })}>
                  <Icon name="trash" size={12} />
                </button>
              </>
            )}
            {open && (
              <SettlePopover
                mode="paga"
                amount={nextInst?.amount ?? residual}
                target={nextInst ? { kind: 'TAX_INSTALLMENT', id: nextInst.id } : undefined}
                counterparty={title}
                side="DEBIT"
                pending={pay.isPending}
                onClose={() => setOpen(false)}
                onConfirm={(v) => { setOpen(false); pay.mutate({ source: 'TAX', id: nextInst?.id ?? t.id, amount: nextInst?.amount ?? residual, label: title, ...v }); }}
              />
            )}
          </div>
        </td>
      </tr>
      {expanded && inst.length > 0 && (
        <tr className="adm-subrow" style={{ cursor: 'default' }}>
          <td colSpan={5} style={{ padding: 0 }}>
            <div className="adm-sublist">
              {inst.map((i) => <InstallmentLine key={i.id} t={t} i={i} canWrite={canWrite} />)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function InstallmentLine({ t, i, canWrite }: { t: TaxObligation; i: TaxInstallment; canWrite: boolean }) {
  const { pay } = useAdminActions();
  const [open, setOpen] = useState(false);
  const days = daysFromToday(i.dueDate);
  const paid = !!i.paidAt;
  const overdue = !paid && days != null && days < 0;
  const label = `${t.description || TAX_KIND_LABEL[t.kind]} · rata ${i.seq}`;
  return (
    <div className="adm-line">
      <div className="mono text-[12px] mute" style={{ width: 60 }}>rata {i.seq}</div>
      <div className={`mono text-[12px] ${overdue ? 'text-alert' : 'mute'}`} style={{ width: 100 }}>{fmtDate(i.dueDate)}</div>
      <div className="min-w-0 flex-1">
        {paid ? <Chip tone="ok" dot>Pagata · {fmtDate(i.paidAt)}</Chip> : overdue ? <Chip tone="alert" dot>Scaduta</Chip> : <Chip tone="warn" dot>Da pagare</Chip>}
      </div>
      <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(i.amount, true)}</div>
      <div className="relative" style={{ width: 90 }}>
        {canWrite && !paid && (
          <button className="btn btn-sm" disabled={pay.isPending} onClick={() => setOpen((v) => !v)}>
            <Icon name="check" size={10} /> Paga
          </button>
        )}
        {open && (
          <SettlePopover
            mode="paga"
            amount={i.amount}
            target={{ kind: 'TAX_INSTALLMENT', id: i.id }}
            counterparty={t.description || TAX_KIND_LABEL[t.kind]}
            side="DEBIT"
            pending={pay.isPending}
            onClose={() => setOpen(false)}
            onConfirm={(v) => { setOpen(false); pay.mutate({ source: 'TAX', id: i.id, amount: i.amount, label, ...v }); }}
          />
        )}
      </div>
    </div>
  );
}
